// Generic disclosure wrapper for native <details> submenus (dropdown menu type).
// Adds Escape-to-close + close-on-focus-out; the <summary> handles open/close natively.
class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    if (!this.mainDetailsToggle) return;
    this.summary = this.mainDetailsToggle.querySelector('summary');

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('keyup', this.onKeyUp.bind(this));
  }

  onKeyUp(event) {
    if (event.key !== 'Escape' && event.code !== 'Escape') return;
    this.close();
    this.summary?.focus();
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.summary?.setAttribute('aria-expanded', 'false');
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

// Desktop megamenu. The panel opens on HOVER (mouse — shopify-global.js toggles the
// `.mega-menu__content-open` class) and on SPACE (keyboard — here), reusing the SAME
// reveal class so the two paths can't fight each other. The top item stays a real
// link: Enter navigates to its category; Space reveals the panel and keyboard users
// then Tab into it; Esc closes and returns focus to the top link. Using a class (not
// a <details> toggle) avoids the native <details> content-hiding that broke hover.
class HeaderMenu extends HTMLElement {
  constructor() {
    super();
    this.menu = this.querySelector('.mega-menu');
    if (!this.menu) return;
    this.trigger = this.querySelector('a[aria-controls]');
    this.content = this.menu.querySelector('.mega-menu__content');

    this.addEventListener('keydown', this.onKeyDown.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyUp.bind(this));
  }

  get isOpen() {
    return !!this.content?.classList.contains('mega-menu__content-open');
  }

  // Space toggles the panel.
  //
  // This used to open on focusin, which meant a full-screen panel unfolded under
  // every top-level item as a keyboard user tabbed along the nav — nobody asked
  // for it, and there was no way to move past an item without opening it. Making
  // it an explicit key leaves the user in charge; aria-haspopup on the trigger is
  // what tells them the panel is available to open.
  onKeyDown(event) {
    if (event.key !== ' ' && event.code !== 'Space') return;
    // Only from the top-level item — inside the panel, Space belongs to whatever
    // control has focus.
    if (event.target !== this.trigger) return;
    // Space does nothing on a link natively, it just scrolls the page.
    event.preventDefault();
    if (this.isOpen) this.close();
    else this.open();
  }

  open() {
    if (!this.content) return;
    this.content.classList.add('mega-menu__content-open');
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.lazyLoadMedia();
  }

  close() {
    this.content?.classList.remove('mega-menu__content-open');
    this.trigger?.setAttribute('aria-expanded', 'false');
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyUp(event) {
    if (event.key !== 'Escape' && event.code !== 'Escape') return;
    this.close();
    this.trigger?.focus();
  }

  lazyLoadMedia() {
    this.content
      ?.querySelectorAll('img[data-src], video[data-src]')
      .forEach((el) => {
        const src = el.getAttribute('data-src');
        if (!src) return;
        el.setAttribute('src', src);
        el.removeAttribute('data-src');
      });
  }
}

customElements.define('header-menu', HeaderMenu);

